import React, { useEffect, useState, useCallback } from 'react';
import { Progress, Alert, Card, Button, Typography } from 'antd';
import type { SimulationDetail, SimulationEvent } from '../../store/simulation/types';
import { palette } from '@shipium-corp/shipium-ui-theme';
import { format } from 'date-fns/format';

import { CheckCircleFilled, SyncOutlined } from '@ant-design/icons';
import { useStoreActions } from '../../store';
import { resolveSimulationServiceVersion } from '../../store/simulation/resolveSimulationServiceVersion';

export interface SimulationProgressProps {
  simulation?: SimulationDetail;
}

const { Text } = Typography;
const REFRESH_INTERVAL = 1000 * 60;
const GENERIC_EXCEPTION_MESSAGE = 'An unrecoverable error occured.  Please check simulation events for more detail.';

// v2 records the failure reason on the exception event's metadata; some paths write none (fallback).
const latestExceptionReason = (events?: SimulationEvent[]): string | undefined => {
  const reason = (events ?? [])
    .filter((event) => typeof event.simulationEventMetaData?.error === 'string')
    .sort((a, b) => new Date(b.eventTimestamp).getTime() - new Date(a.eventTimestamp).getTime())[0]
    ?.simulationEventMetaData?.error;
  return typeof reason === 'string' ? reason : undefined;
};

export const SimulationProgress = ({ simulation }: SimulationProgressProps) => {
  const fetchSimulationDetail = useStoreActions((actions) => actions.simulation.fetchSimulationDetail);
  const isProcessing = simulation?.simulationStatus === 'processing';
  const [time, setTime] = useState(Date.now());
  const nextRefresh = format(time + REFRESH_INTERVAL, 'pp');
  const simulationId = simulation?.simulationId;

  const fetchCallback = useCallback(() => {
    setTime(Date.now());
    fetchSimulationDetail(simulationId);
  }, [fetchSimulationDetail, simulationId]);

  useEffect(() => {
    if (!isProcessing) {
      return;
    }
    const interval = setInterval(() => {
      fetchCallback();
    }, REFRESH_INTERVAL);
    return () => {
      clearInterval(interval);
    };
  }, [isProcessing, fetchCallback]);

  if (!simulation) {
    return null;
  }
  const {
    finalBatchStatusCount = 0,
    expectedBatches = 0,
    completedShipmentCount = 0,
    totalShipmentCount = 0,
    cancelledShipmentCount = 0,
    failedShipmentCount = 0,
  } = simulation;
  const successPercentage = totalShipmentCount > 0 ? (completedShipmentCount * 100) / totalShipmentCount : 0;
  const overallPercentage =
    expectedBatches > 0
      ? ((completedShipmentCount + failedShipmentCount + cancelledShipmentCount) * 100) / totalShipmentCount
      : 0;
  const simulationBatchCreationInProgress = simulation.simulationShipmentBatches?.length === 0;
  const icon =
    !simulationBatchCreationInProgress &&
    finalBatchStatusCount === expectedBatches &&
    simulation.expectedBatches !== null ? (
      <CheckCircleFilled style={{ color: palette.Teal.light, fontSize: '16px' }} />
    ) : (
      <SyncOutlined style={{ color: palette.Teal.light, fontSize: '16px' }} spin />
    );

  const statusText = simulationBatchCreationInProgress ? (
    <Text>Simulation Batch Creation in progress</Text>
  ) : (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <Text style={{ lineHeight: 2 }}>
        {completedShipmentCount?.toLocaleString() || 0} of {totalShipmentCount?.toLocaleString()} simulated shipments{' '}
        <b>completed</b> ({Math.floor(successPercentage)}%)
      </Text>
      {failedShipmentCount > 0 && (
        <Text style={{ padding: 0 }} type={'danger'}>
          {cancelledShipmentCount?.toLocaleString() || 0} of {failedShipmentCount?.toLocaleString()} simulation
          shipments <b>failed</b> ({Math.floor((failedShipmentCount * 100) / totalShipmentCount)}%)
        </Text>
      )}
      {cancelledShipmentCount > 0 && (
        <Text style={{ padding: 0 }} type={'warning'}>
          {cancelledShipmentCount?.toLocaleString() || 0} of {totalShipmentCount?.toLocaleString()} simulation shipments{' '}
          <b>cancelled</b> ({Math.floor((cancelledShipmentCount * 100) / totalShipmentCount)}%)
        </Text>
      )}
    </div>
  );

  if (simulation.simulationStatus === 'archived') {
    return (
      <Alert
        message={
          'This simulation has been Archived.  All simulation data has been deleted.  This record exists for audit purposes.'
        }
        type={'warning'}
      />
    );
  }

  if (simulation.simulationStatus === 'exception') {
    // v2 only: prefer the recorded failure reason; v1 keeps the generic message.
    const isV2 = resolveSimulationServiceVersion(simulation.simulationSetupData) === 'v2';
    const reason = isV2 ? latestExceptionReason(simulation.simulationEvents) : undefined;
    return <Alert message={reason ?? GENERIC_EXCEPTION_MESSAGE} type={'error'} />;
  }

  if (simulation.simulationStatus === 'cancelled') {
    return (
      <Alert
        message={'This simulation has been cancelled.  Please check simulation events for more detail.'}
        type={'warning'}
      />
    );
  }

  if (simulation.simulationStatus === 'completed') {
    return null;
  }
  return (
    <Card bordered={true} style={{ marginLeft: -2, marginTop: 4 }} bodyStyle={{ padding: 8 }}>
      <div style={{ display: 'flex' }}>
        <div style={{ marginLeft: 'auto', order: 2 }}>
          {isProcessing && (
            <Text type="secondary">
              Auto refresh at {nextRefresh}
              <Button style={{ padding: 5 }} onClick={() => fetchCallback()} type="link">
                <small>(refresh now)</small>
              </Button>
            </Text>
          )}
        </div>
        <div style={{ lineHeight: 2.5 }}>{statusText}</div>
      </div>
      <div style={{ display: 'flex' }}>
        <div style={{ marginRight: 5 }}>{icon}</div>
        <div style={{ flexGrow: 1 }}>
          <Progress
            percent={overallPercentage}
            success={{ percent: successPercentage, strokeColor: palette.Teal.light }}
            strokeColor={palette.Gold.light}
            strokeWidth={10}
            showInfo={false}
          />
        </div>
      </div>
    </Card>
  );
};
